import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  alignNiumaLegend,
  isPersistableLegend,
  loadLegendFromSupabase,
  saveLegendItem,
} from "./legendAdapter";
import { FALLBACK_LEGEND } from "./legendFallback";
import type { BoardLegendItem, BoardLegendPatch } from "./legendTypes";

type BoardLegendApi = {
  items: BoardLegendItem[];
  source: BoardLegendItem["source"];
  error: string | null;
  setError: (error: string | null) => void;
  save: (
    item: BoardLegendItem,
    patch: BoardLegendPatch,
  ) => Promise<BoardLegendItem>;
  refresh: () => Promise<void>;
};

const BoardLegendContext = createContext<BoardLegendApi | null>(null);

function useBoardLegendState(): BoardLegendApi {
  const [items, setItems] = useState<BoardLegendItem[]>(FALLBACK_LEGEND);
  const [source, setSource] = useState<BoardLegendItem["source"]>("fallback");
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const rows = await loadLegendFromSupabase();
    if (!rows?.length) return;
    setItems(alignNiumaLegend(rows));
    setSource("db");
    setError(null);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const save = useCallback(
    async (item: BoardLegendItem, patch: BoardLegendPatch) => {
      if (!isPersistableLegend(item) || item.id == null) {
        throw new Error("图例尚未从库加载，无法保存");
      }
      const updated = await saveLegendItem(item.id, patch);
      setItems((current) =>
        current.map((row) => (row.id === updated.id ? updated : row)),
      );
      setSource("db");
      return updated;
    },
    [],
  );

  return { items, source, error, setError, save, refresh };
}

export function BoardLegendProvider({ children }: { children: ReactNode }) {
  const value = useBoardLegendState();
  return createElement(BoardLegendContext.Provider, { value }, children);
}

export function useBoardLegend() {
  const ctx = useContext(BoardLegendContext);
  if (!ctx) {
    throw new Error("useBoardLegend must be used inside BoardLegendProvider");
  }
  return ctx;
}
